import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import STUtils from "../../assets/styles/utility.module.scss";
import STMemberEntryFrom from "../../assets/styles/organisms/memberEntryForm.module.scss";
import { MemberEntryForm as TMemberEntryForm } from "../../types/form/memberEntryForm";
import { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import STButton from "../../assets/styles/atoms/button.module.scss";
import PAGE_CONFIG from "../../config/page";
import { fetchMessageSetting } from "../../utils/api/fetchMessageSetting";
import { PANEL } from "../../types/TAppPanel";
import getAddressData from "../../utils/getAddressData";
import { routes } from "../../router/Router";
import HtmlMessage from "../atoms/HtmlMessage";
import TextForm from "../molecules/reactHookForms/TextForm";
import PasswordForm from "../molecules/reactHookForms/PasswordForm";
import useMultipleClickPreventer from "../molecules/UseMultipleClickPreventer";
import { TApiMemberRegisterResult, TApiMemberRegisterValidateErrors, postMemberRegister } from "../../utils/api/postMemberRegister";
import TApiResult from "../../types/api/TApiResult";
import SelectForm from "../molecules/reactHookForms/SelectForm";
import { TAppPrefectureNames } from "../../types/TAppPrefectureNames";
import { SelectBoxOption } from "../../types/form/selectBoxOption";
import schema from "../../utils/form/memberEntryValidation";
import DualInputForm from "../molecules/reactHookForms/DualInputForm";
import ErrorMessage from "../atoms/form/reactHookForms/ErrorMessage";
import TextFormWithButton from "../molecules/reactHookForms/TextFormWithButton";
import { MEMBER_ENTEY_FORM_ITEM, TAppMemberEntryFormItem } from "../../types/TAppMemberEntryFormItem";

const transformRegisterErrorResult: {[key in TAppMemberEntryFormItem]: {index: number, formName: keyof typeof MEMBER_ENTEY_FORM_ITEM}} = {
  sei: {index: 1, formName: "sei"},
  mei: {index: 2, formName: "mei"},
  kana_sei: {index: 3, formName: "kanaSei"},
  kana_mei: {index: 4, formName: "kanaMei"},
  contractor_sei: {index: 5, formName: "contractorSei"},
  contractor_mei: {index: 6, formName: "contractorMei"},
  email: {index: 7, formName: "email"},
  tel: {index: 8, formName: "tel"},
  zipcode: {index: 9, formName: "zipcode"},
  pref_name: {index: 10, formName: "prefName"},
  city_name: {index: 11, formName: "cityName"},
  address: {index: 12, formName: "address"},
  instagram_ownerinput: {index: 13, formName: "instagramOwnerinput"},
  password: {index: 14, formName: "password"},
  passwordConfirm: {index: 15, formName: "passwordConfirm"},
};

type Props = {
  email: string;
  setIsApplied: (isApplied: boolean) => void;
};

const MemberEntryForm = (props: Props) => {
  const {
    register,
    watch,
    setError,
    setFocus,
    trigger,
    setValue,
    getValues,
    handleSubmit,
    formState: {
      errors
    },
  } = useForm<TMemberEntryForm>({
    mode: 'all',
    resolver: yupResolver(schema),
  });

  const isApply = watch('apply', false);
  const [customMessage, setCustomMessage] = useState("");
  const history = useHistory();

  useEffect(() => {
    setValue('email', props.email);
  }, [props.email, setValue]);

  const options: SelectBoxOption[] = TAppPrefectureNames.map((v, i) => {
    return { id: i, value: v, title: v };
  });

  const handlePostCodeBtnClick = () => {
    getAddressData(
      getValues('zipcode'),
      (prefecture: string, city: string, area: string, street: string) => {
        const address = `${area}${street}`.trim();
        setValue("prefName", prefecture);
        setValue("cityName", city);
        setValue("address", address);
        trigger('prefName');
        trigger('cityName');
        trigger('address');
      }
    );
  };

  const onSubmit: SubmitHandler<TMemberEntryForm> = useMultipleClickPreventer(() => {
    // passwordConfirmとapplyは会員登録に不要なリクエストパラメータなので抜く。
    const { passwordConfirm, apply, zipcode, ...formParam } = getValues();
    const newZipcode = zipcode.replace(/-/g, "");

    return postMemberRegister({
      ...formParam,
      zipcode: newZipcode,
    }).then(
      (
        res: TApiResult &
          TApiMemberRegisterResult &
          TApiMemberRegisterValidateErrors
      ) => {
        if (!res.isSuccess || !res.result || res.validateErrors.length !== 0) {
          let focusedIndex = 16;
          let focusedFormName: keyof typeof MEMBER_ENTEY_FORM_ITEM | undefined;
          res.validateErrors.forEach(({key, messages}) => {
            const {formName, index} = transformRegisterErrorResult[key];

            focusedFormName = index < focusedIndex ? formName : focusedFormName;
            focusedIndex = index < focusedIndex ? index : focusedIndex;

            setError(formName, {
              type: "manual",
              message: messages[0],
            });
          });

          if (focusedFormName !== undefined) {
            setFocus(focusedFormName)
          };
      } else {
          props.setIsApplied(true);
        }
      }
    );
  });

  useEffect(() => {
    fetchMessageSetting(PANEL.memberEntry).then((res) => {
      if (!res.isSuccess || !res.result) return history.push(routes.error.path);
      const newMessage =
        res.result.length !== 0 ? res.result[0].letter_content : "";
      setCustomMessage(newMessage);
    });
  }, []);

  return (
    <div
      className={`${STMemberEntryFrom.member_entry_form} ${STUtils.container_form}`}
    >
      <h1 className={STMemberEntryFrom.member_entry_form_title}>新規会員登録</h1>
      <div className={STMemberEntryFrom.member_entry_form_custom_message}>
        {customMessage && <HtmlMessage message={customMessage} />}
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DualInputForm
          label={{
            parent: "名前",
            children: {
              first: "姓",
              second: "名"
            }
          }}
          formName={{
            first: "sei",
            second: "mei"
          }}
          register={register}
          errors={errors}
          className={STMemberEntryFrom.member_entry_form_container}
          required={true}
          placeholder={{
            first: "田中",
            second: "太郎"
          }}
        />
        <DualInputForm
          label={{
            parent: "ふりがな",
            children: {
              first: "姓",
              second: "名"
            }
          }}
          formName={{
            first: "kanaSei",
            second: "kanaMei"
          }}
          register={register}
          errors={errors}
          className={STMemberEntryFrom.member_entry_form_container}
          required={true}
          placeholder={{
            first: "たなか",
            second: "たろう"
          }}
        />
        <DualInputForm
          label={{
            parent: "契約者名義",
            children: {
              first: "姓",
              second: "名"
            }
          }}
          formName={{
            first: "contractorSei",
            second: "contractorMei"
          }}
          register={register}
          errors={errors}
          className={STMemberEntryFrom.member_entry_form_container}
          placeholder={{
            first: "田中",
            second: "太郎"
          }}
          optionInformation="ご本人様と異なる場合のみ、ご入力ください。"
        />
        <div className={STMemberEntryFrom.member_entry_form_container}>
          <TextForm
            label="メールアドレス"
            formName="email"
            register={register}
            errors={errors}
            placeholder="test@example.com"
            isReadOnly={true}
            defaultValue={props.email}
          />
        </div>
        <div className={STMemberEntryFrom.member_entry_form_container}>
          <TextForm
            label="電話番号"
            formName="tel"
            register={register}
            errors={errors}
            required={true}
            placeholder="00000000000"
            optionInformation="ハイフンなしで入力してください。"
          />
        </div>
        <div className={STMemberEntryFrom.member_entry_form_container}>
          <TextFormWithButton
            label="郵便番号"
            formName="zipcode"
            register={register}
            errors={errors}
            required={true}
            placeholder="0000000"
            optionInformation="新住所がお決まりの方は新住所をご入力ください。"
            onClick={handlePostCodeBtnClick}
            buttonName="住所検索"
          />
        </div>
        <div className={STMemberEntryFrom.member_entry_form_container}>
          <SelectForm
            label="都道府県"
            formName="prefName"
            register={register}
            options={options}
            errors={errors}
            required={true}
            placeholder="東京都"
          />
        </div>
        <div className={STMemberEntryFrom.member_entry_form_container}>
          <TextForm
            label="市区町村"
            formName="cityName"
            register={register}
            errors={errors}
            required={true}
            placeholder="入力例市"
          />
        </div>
        <div className={STMemberEntryFrom.member_entry_form_container}>
          <TextForm
            label="町名・番地"
            formName="address"
            register={register}
            errors={errors}
            required={true}
            placeholder="入力例町1-2-3"
          />
        </div>
        <div className={STMemberEntryFrom.member_entry_form_container}>
          <TextForm
            label="instagramユーザネーム"
            formName="instagramOwnerinput"
            register={register}
            errors={errors}
            placeholder="instagram"
          />
        </div>
        <div className={STMemberEntryFrom.member_entry_form_container}>
          <PasswordForm
            label="パスワード"
            formName="password"
            register={register}
            errors={errors}
            required={true}
            placeholder="●●●●●●●●●●●●"
            onChange={() => trigger('passwordConfirm')}
          />
          <div className={STMemberEntryFrom.member_entry_form_password_message}>
            <p>以下を満たすパスワードをご入力ください。</p>
              <ul>
                <li>半角英数字記号（スペースを除く）</li>
                <li>8文字以上20文字以下</li>
                <li>英字と数字が1文字以上含まれる</li>
              </ul>
          </div>
        </div>
        <div className={STMemberEntryFrom.member_entry_form_container}>
          <PasswordForm
            label="確認用パスワード"
            formName="passwordConfirm"
            register={register}
            errors={errors}
            required={true}
            placeholder="●●●●●●●●●●●●"
            optionInformation="もう一度パスワードを入力してください。"
          />
        </div>
        <div className={STMemberEntryFrom.member_entry_form_container}>
          <div className={STMemberEntryFrom.member_entry_form_password_message}>
            <Link
              to={routes.term.path}
              target="_blank"
              className={STMemberEntryFrom.point_request_card_agreement_text_link}
            >
              規約
            </Link>
            および
            <Link
              to={routes.privacy.path}
              target="_blank"
              className={STMemberEntryFrom.point_request_card_agreement_text_link}
            >
              プライバシーポリシー
            </Link>
            について確認の上、同意します。
            <input
              type="checkbox"
              id="apply"
              {...register('apply')}
              className={`${STMemberEntryFrom.point_request_card_agreement_checkbox} ${PAGE_CONFIG.STATIC_CLASSES.CHECKBOX}`}
              data-testid="entry-form_apply"
            />
            <label
              className={STMemberEntryFrom.point_request_card_agreement_label}
              htmlFor="apply"
            >
              同意する
            </label>
            {errors.apply && <ErrorMessage name="apply" message={errors.apply?.message ?? ""} />}
          </div>
        </div>
        <div
          className={
            isApply
              ? `${STMemberEntryFrom.submit_container} ${STButton.primary} ${PAGE_CONFIG.STATIC_CLASSES.BUTTON_PRIMARY}`
              : `${STMemberEntryFrom.submit_container} ${STButton.disable}`
          }
        >
          <button
            type="submit"
            disabled={!isApply}
            data-testid="entry-form_submit"
            className={`${STMemberEntryFrom.submit_button}`}
          >
            登録
          </button>
        </div>
      </form>
    </div>
  );
};

export default MemberEntryForm;
